import React, { useEffect, useRef, useState, useMemo } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { useInteractionChart } from './service';

/* ─────────────── Sabitler ─────────────── */

type RangeKey = 'today' | 'this_week' | 'last_week' | 'this_month' | 'this_quarter' | 'this_year';

const RANGES: { key: RangeKey; label: string; short: string }[] = [
  { key: 'today', label: 'Bugün', short: 'Bugün' },
  { key: 'this_week', label: 'Bu Hafta', short: 'Hafta' },
  { key: 'last_week', label: 'Geçen Hafta', short: 'G.Hafta' },
  { key: 'this_month', label: 'Bu Ay', short: 'Ay' },
  { key: 'this_quarter', label: 'Bu Çeyrek', short: 'Çeyrek' },
  { key: 'this_year', label: 'Bu Yıl', short: 'Yıl' },
];

const INTERACTION_META = {
  visits: { label: 'Ziyaret', color: '#22c55e', icon: 'fa-route' },
  emails: { label: 'E-posta', color: '#3b82f6', icon: 'fa-envelope' },
  calls: { label: 'Arama', color: '#f59e0b', icon: 'fa-phone-alt' },
  socials: { label: 'Sosyal Medya', color: '#8b5cf6', icon: 'fa-share-alt' },
};

type InteractionKey = keyof typeof INTERACTION_META;

/* ─────────────── Animasyonlu Sayaç ─────────────── */

const CountUp: React.FC<{ value: number }> = ({ value }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef<number>();

  useEffect(() => {
    const dur = 800;
    const start = performance.now();
    const from = display;
    const animate = (now: number) => {
      const t = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (value - from) * ease));
      if (t < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => {
      if (ref.current) cancelAnimationFrame(ref.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <>{display.toLocaleString('tr-TR')}</>;
};

/* ─────────────── Mini Stat Kartı ─────────────── */

const StatCard: React.FC<{
  type: InteractionKey;
  count: number;
  total: number;
  active: boolean;
  onClick: () => void;
}> = ({ type, count, total, active, onClick }) => {
  const meta = INTERACTION_META[type];
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <button
      onClick={onClick}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        background: active ? `${meta.color}08` : '#fff',
        border: `1.5px solid ${active ? meta.color : '#f1f5f9'}`,
        borderRadius: 12,
        padding: '14px 12px 12px',
        cursor: 'pointer',
        transition: 'all .25s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = `${meta.color}60`;
          e.currentTarget.style.background = `${meta.color}05`;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = '#f1f5f9';
          e.currentTarget.style.background = '#fff';
        }
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: `${meta.color}14`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all .25s',
        }}
      >
        <i className={`fas ${meta.icon}`} style={{ color: meta.color, fontSize: '.85rem' }} />
      </div>
      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
        <CountUp value={count} />
      </span>
      <span style={{ fontSize: '.68rem', fontWeight: 600, color: '#64748b', letterSpacing: '.2px' }}>{meta.label}</span>
      <span style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: 500 }}>%{pct}</span>
    </button>
  );
};

/* ─────────────── Ana Widget ─────────────── */

export const DashboardInteractionsWidget: React.FC = () => {
  const [range, setRange] = useState<RangeKey>('this_week');
  const [activeTypes, setActiveTypes] = useState<Set<InteractionKey>>(new Set<InteractionKey>(['visits', 'emails', 'calls', 'socials']));
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<ApexCharts | null>(null);

  const { data, isLoading, error } = useInteractionChart(range, (err: any) => {
    console.error('Etkileşim grafiği yüklenirken hata:', err);
  });

  const toggleType = (type: InteractionKey) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type); // En az 1 tür aktif kalmalı
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Grafik verileri
  const chartData = useMemo(() => {
    if (!data?.series) return null;
    const labels = data.series.map((s: any) => s.label);
    const series: { name: string; data: number[]; color: string }[] = [];

    const types: InteractionKey[] = ['visits', 'emails', 'calls', 'socials'];
    types.forEach((type) => {
      if (activeTypes.has(type)) {
        series.push({
          name: INTERACTION_META[type].label,
          data: data.series.map((s: any) => s[type]),
          color: INTERACTION_META[type].color,
        });
      }
    });

    return { labels, series };
  }, [data, activeTypes]);

  // ApexCharts render
  useEffect(() => {
    if (!chartRef.current || !chartData || chartData.series.length === 0) return;

    const options: ApexOptions = {
      chart: {
        type: 'area',
        height: 320,
        fontFamily: 'inherit',
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 600,
          dynamicAnimation: { enabled: true, speed: 400 },
        },
        dropShadow: {
          enabled: true,
          top: 3,
          left: 0,
          blur: 6,
          opacity: 0.08,
          color: chartData.series[0]?.color || '#000',
        },
      },
      series: chartData.series.map((s) => ({ name: s.name, data: s.data })),
      xaxis: {
        categories: chartData.labels,
        labels: {
          style: { fontSize: '11px', colors: '#94a3b8', fontWeight: 500 },
          rotate: 0,
          trim: true,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: {
          show: true,
          stroke: { color: '#e2e8f0', width: 1, dashArray: 3 },
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: '11px', colors: '#94a3b8', fontWeight: 500 },
          formatter: (val: number) => Math.round(val).toString(),
        },
        min: 0,
        forceNiceScale: true,
      },
      colors: chartData.series.map((s) => s.color),
      stroke: {
        curve: 'smooth',
        width: 2.5,
        lineCap: 'round',
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 8, right: 8, top: 0, bottom: 0 },
      },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
        style: { fontSize: '12px' },
        y: {
          formatter: (val: number) => `${val} etkileşim`,
        },
        marker: { show: true },
      },
      markers: {
        size: 0,
        hover: { size: 5, sizeOffset: 2 },
      },
    };

    if (chartInstance.current) {
      chartInstance.current.updateOptions(options, true, true);
    } else {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    }

    return () => {};
  }, [chartData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      chartInstance.current?.destroy();
      chartInstance.current = null;
    };
  }, []);

  // Chart'ı yeniden oluştur (range değişince)
  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }
  }, [range]);

  /* ─── Loading skeleton ─── */
  if (isLoading) {
    return (
      <div className="col-12 mb-6">
        <div style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', border: '1px solid #f1f5f9' }}>
          <div className="placeholder-glow">
            <span className="placeholder rounded-2 mb-3" style={{ width: 200, height: 28, display: 'block' }} />
            <div className="d-flex gap-2 mb-4">
              {[1, 2, 3, 4].map((k) => (
                <span key={k} className="placeholder rounded-3" style={{ flex: 1, height: 90 }} />
              ))}
            </div>
            <span className="placeholder rounded-2" style={{ width: '100%', height: 300, display: 'block' }} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="col-12 mb-6">
        <div className="alert alert-danger mb-0" style={{ borderRadius: 16 }}>
          Etkileşim verileri yüklenirken hata oluştu
        </div>
      </div>
    );
  }

  const totals = data.totals || { visits: 0, emails: 0, calls: 0, socials: 0, total: 0 };
  const topUsers = data.topUsers || [];

  /* ─── Render ─── */
  return (
    <div className="col-12 mb-6">
      <style>{`
        .int-range-btn {
          padding: 6px 14px;
          border: 1px solid transparent;
          border-radius: 8px;
          font-weight: 600;
          font-size: .75rem;
          cursor: pointer;
          transition: all .2s ease;
          background: none;
          color: #94a3b8;
          white-space: nowrap;
        }
        .int-range-btn.active {
          background: #f8fafc;
          color: #1e293b;
          border-color: #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,.04);
        }
        .int-range-btn:not(.active):hover {
          color: #64748b;
          background: #f8fafc;
        }
        @media (max-width: 768px) {
          .int-stat-grid { flex-wrap: wrap !important; }
          .int-stat-grid > button { flex: 1 1 45% !important; }
          .int-range-wrap { overflow-x: auto; flex-wrap: nowrap !important; -webkit-overflow-scrolling: touch; }
          .int-range-wrap::-webkit-scrollbar { display: none; }
        }
      `}</style>

      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid #f1f5f9',
          boxShadow: '0 1px 2px rgba(0,0,0,.03)',
        }}
      >
        {/* ── Header ── */}
        <div style={{ padding: '24px 28px 0' }}>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="fas fa-chart-line" style={{ color: '#fff', fontSize: '.95rem' }} />
              </div>
              <div>
                <h5 style={{ margin: 0, fontSize: '.95rem', fontWeight: 700, color: '#0f172a' }}>Müşteri Etkileşimleri</h5>
                <span style={{ fontSize: '.72rem', color: '#94a3b8', fontWeight: 500 }}>
                  Toplam{' '}
                  <strong style={{ color: '#475569' }}>
                    <CountUp value={totals.total} />
                  </strong>{' '}
                  etkileşim
                </span>
              </div>
            </div>

            {/* Range selector */}
            <div className="d-flex align-items-center gap-1 int-range-wrap">
              {RANGES.map((r) => (
                <button key={r.key} className={`int-range-btn ${range === r.key ? 'active' : ''}`} onClick={() => setRange(r.key)}>
                  <span className="d-none d-md-inline">{r.label}</span>
                  <span className="d-md-none">{r.short}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Stat kartları ── */}
        <div style={{ padding: '0 28px' }}>
          <div className="d-flex gap-2 mb-4 int-stat-grid" style={{ display: 'flex' }}>
            {(Object.keys(INTERACTION_META) as InteractionKey[]).map((type) => (
              <StatCard key={type} type={type} count={totals[type]} total={totals.total} active={activeTypes.has(type)} onClick={() => toggleType(type)} />
            ))}
          </div>
        </div>

        {/* ── Grafik ── */}
        <div style={{ padding: '0 20px 8px' }}>
          {data.series?.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 0',
                color: '#94a3b8',
                fontSize: '.9rem',
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: 10, color: '#e2e8f0' }}>
                <i className="fas fa-chart-area" />
              </div>
              <span>Bu dönemde etkileşim kaydı bulunmuyor</span>
            </div>
          ) : (
            <div ref={chartRef} style={{ minHeight: 320 }} />
          )}
        </div>

        {/* ── Alt: En aktif kullanıcılar ── */}
        {topUsers.length > 0 && (
          <div style={{ padding: '0 28px 20px', borderTop: '1px solid #f8fafc' }}>
            <div style={{ paddingTop: 16 }}>
              <span style={{ fontSize: '.7rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '.5px', textTransform: 'uppercase' }}>En Aktif Kullanıcılar</span>
              <div className="d-flex flex-wrap gap-3 mt-2">
                {topUsers.map((u: any, i: number) => (
                  <div key={u.userId} className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444'][i] || '#94a3b8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '.65rem',
                        fontWeight: 700,
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      {u.name
                        ?.split(' ')
                        .map((w: string) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase() || '?'}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '.72rem',
                          fontWeight: 600,
                          color: '#334155',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 120,
                        }}
                      >
                        {u.name}
                      </div>
                      <div style={{ fontSize: '.62rem', color: '#94a3b8', fontWeight: 500 }}>{u.total} etkileşim</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardInteractionsWidget;
